import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import classes from '../table.module.scss';
import { CellType, selectOption, TableHeadType } from '../../../../types/Common';
import commafy from '../../../../utils/commafyHelper';
import { UserStatus } from '../../../../types/User';
import AttachmentRoundedIcon from '@mui/icons-material/AttachmentRounded';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { getResultDates, getResultTestByTestId } from '../../../../apis/user';
import { setResultTest } from '../../../../store/user/user.reducer';

interface CustomTableRowProps {
  tableHead: TableHeadType[];
  row: any;
  index: number;
  rowsIndex: number;
  hasSelect: boolean;
  setSelected?: (id: any) => void;
  selected?: number[];
}

const CustomTableRow = (props: CustomTableRowProps) => {
  const dispatch = useDispatch();
  const { tableHead, row, index, selected = [], setSelected, hasSelect, rowsIndex = 0 } = props;
  const [loading, setLoading] = useState(false);
  const { testId, lastTestId, name, lastName, userId } = row || {};

  const getTestResult = () => {
    const fullName = name + ' ' + lastName;
    setLoading(true);
    getResultDates(userId)
      .then((res) => {
        const tabs = res;

        getResultTestByTestId(lastTestId || testId)
          .then((res) =>
            dispatch(setResultTest({ openResultDialog: true, resultTest: res, fullName, tabs })),
          )
          .finally(() => setLoading(false));
      })
      .catch(() => setLoading(false));
  };

  const findAndTranslateValue = (value: any, options: selectOption[]) =>
    options.find((i: selectOption) => i.value === value)?.name || '---';

  const getCellValue = (key: string, type: CellType, options?: selectOption[], action?: any) => {
    let defaultValue = row?.[key] === 0 ? 0 : row[key] || '---';

    if (type === CellType.ROW) defaultValue = rowsIndex + (index + 1);

    if (type === CellType.INFO)
      defaultValue =
        defaultValue?.toString().length > 50 ? '...' + defaultValue.slice(0, 50) : defaultValue;

    if (type === CellType.ACTION) defaultValue = action(row);

    if (type === CellType.ENUM && options)
      defaultValue = findAndTranslateValue(defaultValue, options);

    if (type === CellType.PRICE) defaultValue = commafy(defaultValue);

    if (type === CellType.FLOAT) defaultValue = defaultValue ? defaultValue.toFixed(2) : 0;

    if (type === CellType.TEST)
      defaultValue =
        testId || lastTestId ? (
          <IconButton onClick={getTestResult} disabled={loading}>
            {loading ? (
              <CircularProgress size={26} color='primary' />
            ) : (
              <AttachmentRoundedIcon color='primary' />
            )}
          </IconButton>
        ) : (
          <span />
        );

    if (type === CellType.MOBILE)
      defaultValue = (
        <a href={`tel:${defaultValue}`} style={{ textDecoration: 'none', color: '#0a33f1' }}>
          {defaultValue}
        </a>
      );

    if (type === CellType.DATE)
      defaultValue =
        defaultValue.slice(0, 10) + ' - ' + defaultValue.slice(10, defaultValue?.toString().length);

    if (type === CellType.REGISTER_DATE)
      defaultValue =
        defaultValue.slice(0, 4) + '/' + defaultValue.slice(4, 6) + '/' + defaultValue.slice(6, 8);

    if (type === CellType.STATUS)
      defaultValue =
        defaultValue === UserStatus.ACTIVE ? (
          <Box className={classes.badge} bgcolor='success.light'>
            <Typography color='success.main'>فعال</Typography>
          </Box>
        ) : (
          <Box className={classes.badge} bgcolor='error.light'>
            <Typography color='error.main'>غیر فعال</Typography>
          </Box>
        );

    return defaultValue;
  };

  const isSelected = (id: number) => selected?.indexOf(id) !== -1;

  const isItemSelected = isSelected(row.id);
  const labelId = `enhanced-table-checkbox-${index}`;

  const handleClick = (event: React.MouseEvent<unknown>, id: number) => {
    const selectedIndex = selected?.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected?.(newSelected);
  };

  return (
    <TableRow
      key={row.name}
      sx={{
        '&:last-child td, &:last-child th': { border: 0 },
        backgroundColor: isItemSelected ? '#FFF1E6' : 'transparent',
      }}
    >
      {hasSelect && (
        <TableCell padding='checkbox' onClick={(event) => handleClick(event, row.id)}>
          <Checkbox
            color='primary'
            checked={isItemSelected}
            inputProps={{
              'aria-labelledby': labelId,
            }}
          />
        </TableCell>
      )}

      {tableHead.map((item: TableHeadType) => {
        const { key, type, options, action } = item;
        return (
          <TableCell key={item.key} component='th' scope='row' align='right' dir='ltr'>
            {getCellValue(key, type, options, action)}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

export default CustomTableRow;
